import { create } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { type PlanTimeframe } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import { getProcurementPlanByFilter } from '@ebalance/proto/bffuser/v1/procurement_plan_service-ProcurementPlanService_connectquery'
import {
  GetProcurementPlanByFilterRequestSchema,
  type GetProcurementPlanByFilterResponse,
} from '@ebalance/proto/bffuser/v1/procurement_plan_service_pb'
import type { UseQueryResult } from '@tanstack/react-query'

import { toProtoDate } from '~/models/dateTime'

type UseGetProcurementPlanByFilterParams = {
  targetPeriodStartDate: Date
  areaCode: AreaCode
  bgCode: string
  /** undefined の時は断面で絞り込まない (単日断面を横断した最新版が返る)。 */
  timeframe: PlanTimeframe | undefined
  /** false の間はクエリを発火しない (= フィルタ未変更時)。 */
  enabled: boolean
}

/**
 * ProcurementPlanService.GetProcurementPlanByFilter を呼ぶ hook。
 * 対象日・エリアが変更された時に、同一 BG コードの計画を検索する
 * (断面で絞り込むかは呼び出し元が決める)。該当なしは
 * ConnectError (NOT_FOUND) として観測される (= isError / error.code)。
 *
 * global QueryClient の `throwOnError: true` を本 query だけ `false` に上書きする。
 * NOT_FOUND (該当計画なし) は AC-06 のとおり呼び出し元がフィルタバーを維持した
 * インライン表示で扱うため、ErrorLayout (全画面エラー) に飛ばさない。
 * 注意: テスト用 QueryClient (testUtils) は throwOnError: false を強制するため、
 * この上書きの欠落は F5 テストでは検出できない (プロダクトのみで全画面エラー化する)。
 */
export function useGetProcurementPlanByFilter({
  targetPeriodStartDate,
  areaCode,
  bgCode,
  timeframe,
  enabled,
}: UseGetProcurementPlanByFilterParams): UseQueryResult<
  GetProcurementPlanByFilterResponse,
  ConnectError
> {
  return useQuery(
    getProcurementPlanByFilter,
    create(GetProcurementPlanByFilterRequestSchema, {
      targetPeriodStartDate: toProtoDate(targetPeriodStartDate),
      areaCode,
      bgCode,
      timeframe,
    }),
    { enabled, throwOnError: false },
  )
}
